import type { ReactNode } from 'react';

import Container from '@/components/public/ui/Container';

type SectionTone = 'paper' | 'ink';

interface SectionProps {
  /** `paper` (default) sits on the page; `ink` is a dark institutional band. */
  tone?: SectionTone;
  /** Vertical padding step. */
  spacing?: 'sm' | 'md' | 'lg';
  /** Skip the inner Container (full-bleed content manages its own width). */
  bleed?: boolean;
  id?: string;
  className?: string;
  containerClassName?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  children: ReactNode;
}

const spacingClass = {
  sm: 'py-10 md:py-12',
  md: 'py-14 md:py-20',
  lg: 'py-20 md:py-28',
};

// Canonical page section: vertical rhythm + optional dark `ink` band + the
// standard container. Compose pages from <Section> instead of bare <section>.
export default function Section({
  tone = 'paper',
  spacing = 'md',
  bleed = false,
  id,
  className = '',
  containerClassName = '',
  children,
  ...aria
}: SectionProps) {
  return (
    <section
      id={id}
      className={`${tone === 'ink' ? 'section-ink' : ''} ${spacingClass[spacing]} ${className}`}
      {...aria}
    >
      {bleed ? children : <Container className={containerClassName}>{children}</Container>}
    </section>
  );
}
